import React, { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useAuthState } from '../../hooks/auth';
import { useUserDetailsQuery } from '../../gql/hooks';
import { authService } from '../../services';
import { Avatar, Button, Dropdown, PaperIcon } from '@nackle/paper';
import { LanguageSelectorLink } from '../languageSelector/LanguageSelector';
import './styles.less';
import { Navigation, routeList } from '../../navigation';
import classNames from 'classnames';
import { ACTIVE_BRAND_CONFIG, IS_HOYA_THEME } from '../../branding';
import { getTranslatedString, Translate } from '../../services/translationService/components/Translate';
import { Spin } from 'antd';
import { useTranslations } from '../../hooks/translations';
import { onResizeEffect } from '../../scripts/utils';
import { BreadcrumbContext } from '../../breadcrumbs/BreadcrumbContext';

function checkIsMobile() {
    return window.innerWidth <= 900;
}

export const HeaderDropdown = forwardRef( ( { setLoggingOutCb, categoryData, categoriesLoading, onOpenCb, brand }, ref ) => {

    const { claims } = useAuthState();
    const { client } = useUserDetailsQuery();
    const { locale } = useTranslations();

    const { clearCrumbs } = useContext( BreadcrumbContext );

    const [ isMobile, setIsMobile ] = useState( checkIsMobile() );
    
    const logOut = async () => {
        clearCrumbs();
        client.resetStore();
        await authService.signOut();
    };

    const isEmployee = useMemo( () => {
        const tags = authService.getTags() ?? [];
        return tags.includes( 'employee' );
    }, [ authService ] );

    const [ open, setOpen ] = useState( false );
    const [ catsOpen, setCatsOpen ] = useState( false );

    const [ initials, name ] = useMemo( () => {

        const inits = claims.name
            .toLocaleUpperCase()
            .split( ' ' )
            .map( n => n?.[ 0 ] )
            .join( '' );

        return [ inits, claims.name ];

    }, [ claims ] );

    function handleLinkClick( { key } ) {
        close();
        Navigation.push( key );
    }

    function handleFileOpen( { key } ) {
        close();
        window.open( key, '_newtab' );
    }

    function handleSignOut() {
        setLoggingOutCb?.( true );
        logOut();
    }

    function close() {
        setOpen( false );
        setCatsOpen( false );
    }
    
    useImperativeHandle( ref, () => ( {
        close: () => {
            close();
        }
    } ), [] );


    function onResize() {
        setIsMobile( checkIsMobile() );
        close();
    }

    useEffect( onResizeEffect( onResize ), [] );

    function toggleOpen() {
        const newOpenState = !open;
        setOpen( newOpenState );
        if ( newOpenState == true ) {
            onOpenCb?.();
        }
    }

    const categoryItems = useMemo( () => 
        <ul className='nkl-dropdown-menu nkl-dropdown-menu-root nkl-dropdown-menu-vertical nkl-dropdown-menu-light category-list'> 
            {   
                categoriesLoading ? (
                    <li key={ 'loading' } 
                        className='nkl-dropdown-menu-item clickable secondary loading' >
                        <Spin />
                    </li> 
                ) : categoryData.length == 0 ? ( 
                    <li key={ 'no-data' } 
                        className='nkl-dropdown-menu-item clickable secondary text-it' >
                        <Translate.Text value='categoryList.noData' />
                    </li> 
                ) : categoryData.map( c => 
                    <li key={ c.id } 
                        className='nkl-dropdown-menu-item clickable secondary' 
                        onClick={ () => handleLinkClick( { key: `/category/${ c.id }` } ) }>
                        { c.name }
                    </li> 
                )
            }
        </ul>, [ categoryData, categoriesLoading, locale ] );


    const items = useMemo( () => [
        {
            key: 'important-mobile',
            className: 'submenu important',
            mobileonly: true,
            label: 
                <div className='user-dropdown nkl-dropdown-menu-item'>
                    <ul className='nkl-dropdown-menu nkl-dropdown-menu-root nkl-dropdown-menu-vertical nkl-dropdown-menu-light shaded'>
                        <li key='name' className='nkl-dropdown-menu-item' >
                            <span className='nkl-dropdown-menu-title-content'>
                                <Avatar shape="square" size="small">{ initials }</Avatar> 
                                <span className='_subtitle highlight'>{ name }</span> 
                            </span>
                        </li>
                        <li key='div' className='nkl-dropdown-menu-item-divider' />
                        <li key='locale' className='nkl-dropdown-menu-item' >
                            <span className='nkl-dropdown-menu-title-content _subtitle'>
                                <LanguageSelectorLink onClick={ () =>  ref.close() } />
                            </span>
                        </li>
                    </ul>
                </div>

        },
        {
            key: 'important-desktop',
            className: 'important shaded',
            desktoponly: true,
            label: 
                <>
                    <Avatar shape="square" size="small">{ initials }</Avatar> 
                    <span className='_subtitle highlight'>{ name }</span> 
                </>

        },

        ...( !isEmployee ? [ {
                key: 'my-account',
                disabled: !ACTIVE_BRAND_CONFIG.myAccountURL,
                label: <span onClick={ () => {
                    if ( ACTIVE_BRAND_CONFIG.myAccountURL ) { 
                        window.location.href = ACTIVE_BRAND_CONFIG.myAccountURL;
                    } else {
                        console.warn( 'User has no account url' );
                    }
                } }>
                    <Translate.Text value='header.nav.myAccount' />
                </span>,
                className: 'clickable _subtitle',
            }, { type: 'divider', desktoponly: true } ] : []
        ),

        {
            key: 'transcript',
            desktoponly: true,
            label: <span onClick={ () => handleLinkClick( { key: '/transcript' } ) }>
                <Translate.Text value='transcript.learning.transcript' />
            </span>,
            className: 'clickable _subtitle',
        }, { type: 'divider', desktoponly: true },
        
        // links
        ...( routeList ?? [] ).filter( i => i?.inMenu && !i.inSecondaryMenu )
            .map( item => item.key == 'category-details' ? {
                key: item.route,
                className: 'submenu clickable openable', 
                mobileonly: true,
                label: // have to do menu like this bc antd has a known error of submenus with mode="inline"
                    <div className='user-dropdown openable nkl-dropdown-menu-item category'>
                        <span className='clickable nkl-dropdown-menu-title-content _subtitle' onClick={ () => setCatsOpen( !catsOpen ) }>
                            <Translate.Text value='header.nav.categories' />
                            
                            { catsOpen ?
                                <PaperIcon key="1" icon="ArrowUp5" collection="ultimate"/>
                                :
                                <PaperIcon key="2" icon="ArrowDown5" collection="ultimate"/>
                            }    
                        </span>
                        { catsOpen && categoryItems }
                    </div>
            } : {
                key: item.route,
                className: 'clickable _subtitle',
                mobileonly: true,
                onClick: handleLinkClick,
                label: <Translate.Text value={ item.translationKey } />
            } )
            .flatMap( item => [ item, { type: 'divider', mobileonly: true } ] ),

        // other

        {
            key: 'locale',
            desktoponly: true,
            className: '_subtitle language-selector',
            label: <LanguageSelectorLink />,
        },
        { type: 'divider', desktoponly: true },
        {
            key: 'sign-out',
            className: 'clickable _subtitle',
            label: <Translate.Text value='header.profile.logOut' />,
            onClick: handleSignOut
        },
        { type: 'divider' },
        // secondary content
        {
            key: IS_HOYA_THEME ? getTranslatedString( 'privacyNotice.link.hoya' ) : getTranslatedString( 'privacyNotice.link.seiko' ),
            className: 'clickable secondary _subtitle',
            label: <Translate.Text value="footer.privacyNotice" />,
            mobileonly: true,
            onClick: handleFileOpen
        },
        {
            key: getTranslatedString( 'privacyPolicy.link' ),
            className: 'clickable secondary _subtitle',
            label: <Translate.Text value="footer.privacyPolicy" />,
            mobileonly: true,
            onClick: handleFileOpen
        },
        {
            key: '/cookie-policy',
            className: 'clickable secondary _subtitle',
            label: <Translate.Text value="footer.cookiePolicy" />,
            mobileonly: true,
            onClick: handleLinkClick
        }
    ].flatMap( x => x ).filter( x => !!x && !( isMobile && x.desktoponly ) && !( !isMobile && x.mobileonly ) )
    , [ isMobile, categoryItems, routeList, catsOpen, locale ] );

    return <Dropdown
        open={ open }
        menu={ { items } }
        placement="bottomRight"  
        className="user-dropdown-mobile"
        overlayClassName={ classNames( 'user-dropdown scroll-fix dropdown header-dropdown noselect', { mobile: isMobile, isOpen: open, isClosed: !open }, brand ) }
    >
        <Button
            className={ classNames( 'user-menu-toggle-button', { mobile: isMobile } ) }
            onClick={ toggleOpen }
            aria-label="User Menu"
        >
            { !isMobile ? <>{ initials }</>
                : open ? <PaperIcon key='mobile-trigger-open' icon="Close" collection="antd-outlined" /> 
                : <PaperIcon key='mobile-trigger-closed' icon="Menu" collection="antd-outlined" />

            }
            
        </Button>
    </Dropdown>;

} );